import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Settings as SettingsIcon, LogOut, Loader2,
  Wallet, CreditCard, Banknote, Plus, Pencil, Trash2, Sparkles, Users,
  ClipboardList, Phone, MapPin, User as UserIcon, Calendar, TrendingUp, RotateCcw, Truck, ImagePlus, Search, MapPinned
} from "lucide-react";
import { DeliveryTab, CardFeeSetting } from "@/components/DeliveryTab";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const clientSortKey = (c) => norm(`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || "") || "\uffff" + c.whatsapp;

function filterClients(clients, q, status) {
  const t = norm(q.trim());
  const digits = q.replace(/\D/g, "");
  return clients
    .filter((c) => status === "all" || (status === "active" ? c.activated : !c.activated))
    .filter((c) => !t || norm(`${c.first_name} ${c.last_name} ${c.name}`).includes(t)
      || (digits && (c.whatsapp || "").replace(/\D/g, "").includes(digits)))
    .sort((a, b) => clientSortKey(a).localeCompare(clientSortKey(b), "fr"));
}
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DriversTab } from "@/components/DriversTab";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "orders", label: "Commandes & Livraisons", icon: ClipboardList },
  { id: "catalog", label: "Catalogue", icon: Package },
  { id: "clients", label: "Clients", icon: Users },
  { id: "delivery", label: "Zones et Frais de Livraison", icon: MapPinned },
  { id: "drivers", label: "Livreurs", icon: Truck },
  { id: "settings", label: "Paramètres", icon: SettingsIcon },
];

const CANCEL_REASONS = [
  "Produit en rupture de stock",
  "Zone de livraison non desservie",
  "Client injoignable",
  "Adresse incorrecte ou introuvable",
  "Horaire de livraison dépassé",
  "Paiement non confirmé",
  "Autre motif",
];

const STATUS_LABEL = {
  awaiting_payment: "Attente paiement", pending: "En attente", assigned: "Attribuée",
  in_transit: "En cours", delivered: "Livrée", cancelled: "Annulée",
};
const STATUS_COLOR = {
  awaiting_payment: "text-slate-400 bg-slate-500/15", pending: "text-amber-300 bg-amber-500/15",
  assigned: "text-blue-300 bg-blue-500/15", in_transit: "text-purple-300 bg-purple-500/15",
  delivered: "text-emerald-300 bg-emerald-500/15", cancelled: "text-rose-300 bg-rose-500/15",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const doLogout = async () => { await logout(); navigate("/staff"); };

  return (
    <div className="min-h-screen bg-[#08080C] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 glass lg:min-h-screen p-3 lg:p-4 lg:sticky lg:top-0 lg:h-screen flex lg:flex-col justify-between items-center lg:items-stretch gap-2">
        <div className="min-w-0 flex-1 lg:flex-none">
          <div className="hidden lg:flex items-center gap-2 mb-8 px-2 pt-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-slate-950" /></div>
            <div>
              <h1 className="text-lg font-bold gold-text leading-none">Avenue Benda</h1>
              <p className="text-[10px] font-mono text-amber-400/60 uppercase">Tableau Vendeur</p>
            </div>
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} title={t.label}
                className={`shrink-0 flex items-center gap-3 px-2.5 lg:px-3 py-2.5 rounded-xl text-sm transition-all ${tab === t.id ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "text-slate-400 hover:text-amber-300"}`}>
                <t.icon className="w-4 h-4" /> <span className="hidden lg:inline">{t.label}</span>
              </button>
            ))}
          </nav>
        </div>
        <button data-testid="admin-logout" onClick={doLogout} className="shrink-0 flex items-center gap-3 px-2.5 lg:px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-rose-300">
          <LogOut className="w-4 h-4" /> <span className="hidden lg:inline">Déconnexion</span>
        </button>
      </aside>

      <main className="flex-1 p-5 lg:p-8 overflow-x-hidden">
        {tab === "overview" && <Overview />}
        {tab === "orders" && <Orders />}
        {tab === "catalog" && <Catalog />}
        {tab === "clients" && <Clients />}
        {tab === "delivery" && <DeliveryTab />}
        {tab === "drivers" && <DriversTab />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="glass rounded-2xl p-5 card-hover">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className={`w-5 h-5 ${accent}`} />
      </div>
      <p className="text-3xl font-bold text-slate-100 font-mono" data-testid={testid}>{value}</p>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("daily");

  const load = useCallback(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!data) return <Loader />;

  const resetMonth = async () => {
    if (!window.confirm("Réinitialiser la caisse du mois ? TOUTES les commandes de l'onglet Commandes & Livraisons et l'historique des retraits seront supprimés. Le nombre de commandes et les montants encaissés par client, ainsi que le fond de caisse de chaque livreur, seront conservés.")) return;
    if (!window.confirm("Action irréversible. Confirmer la réinitialisation ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/register/reset-month");
      toast.success(`Caisse réinitialisée — ${r.data.deleted} commande(s) archivée(s)`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const d = data.daily, m = data.monthly;
  const catTotal = m.by_category.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="fade-up">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Tableau de bord</h2>
      <p className="text-slate-400 text-sm mb-6">Récapitulatif de toute l'activité financière.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="dashboard-filter-daily" onClick={() => setView("daily")}
          className={`px-4 py-2 rounded-xl text-sm border transition-all flex items-center gap-2 ${view === "daily" ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15 hover:text-amber-300"}`}>
          <Calendar className="w-4 h-4" /> Caisse journalière
        </button>
        <button data-testid="dashboard-filter-monthly" onClick={() => setView("monthly")}
          className={`px-4 py-2 rounded-xl text-sm border transition-all flex items-center gap-2 ${view === "monthly" ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15 hover:text-amber-300"}`}>
          <Wallet className="w-4 h-4" /> Caisse mensuelle
        </button>
      </div>

      {view === "daily" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <StatCard icon={TrendingUp} label="CA du jour" value={`${d.total.toFixed(2)} €`} accent="text-amber-400" testid="daily-total" />
            <StatCard icon={Banknote} label="Ventes espèces" value={`${d.cash.toFixed(2)} €`} accent="text-emerald-400" testid="daily-cash" />
            <StatCard icon={CreditCard} label="Ventes carte bleue" value={`${d.card.toFixed(2)} €`} accent="text-blue-400" testid="daily-card" />
          </div>
        </>
      )}

      {view === "monthly" && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-slate-500 font-mono">{m.month}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <StatCard icon={TrendingUp} label="CA du mois" value={`${m.total.toFixed(2)} €`} accent="text-amber-400" testid="monthly-total" />
            <StatCard icon={Banknote} label="Espèces" value={`${m.cash.toFixed(2)} €`} accent="text-emerald-400" testid="monthly-cash" />
            <StatCard icon={CreditCard} label="Carte bleue" value={`${m.card.toFixed(2)} €`} accent="text-blue-400" testid="monthly-card" />
          </div>

          <div className="glass rounded-2xl p-5 mb-4">
            <h4 className="text-sm font-semibold text-slate-200 mb-3">Chiffre d'affaires par catégorie</h4>
            {m.by_category.length === 0 ? <p className="text-xs text-slate-500">Aucune vente ce mois-ci.</p> : (
              <div className="space-y-2.5" data-testid="category-breakdown">
                {m.by_category.map((c) => (
                  <div key={c.category}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-300">{c.category}</span>
                      <span className="font-mono text-amber-300">{c.amount.toFixed(2)} €</span>
                    </div>
                    <div className="h-1.5 bg-[#161926] rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500/60 rounded-full transition-all" style={{ width: `${catTotal > 0 ? (c.amount / catTotal) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button data-testid="reset-month-button" onClick={resetMonth} disabled={busy} variant="outline" className="rounded-xl border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
              <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser la caisse (nouveau mois)
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOrder, setAssignOrder] = useState(null);
  const [filter, setFilter] = useState("active");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/admin/orders"), api.get("/admin/drivers")])
      .then(([o, d]) => { setOrders(o.data); setDrivers(d.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  if (loading) return <Loader />;

  const fullName = (o) => {
    const n = `${o.client_first_name || ""} ${o.client_last_name || ""}`.trim();
    return n || o.client_name || "—";
  };

  const cancelOrder = (o) => { setCancelTarget(o); setCancelReason(""); };
  const confirmCancel = async () => {
    if (!cancelReason) { toast.error("Choisissez un motif d'annulation"); return; }
    try {
      await api.patch(`/admin/orders/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success(`${cancelTarget.order_number} annulée`); setCancelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ACTIVE = ["awaiting_payment", "pending", "assigned", "in_transit"];
  const counts = {
    active: orders.filter((o) => ACTIVE.includes(o.status)).length,
    delivered: orders.filter((o) => o.status === "delivered").length,
    cancelled: orders.filter((o) => o.status === "cancelled").length,
    all: orders.length,
  };
  const filtered = orders.filter((o) =>
    filter === "delivered" ? o.status === "delivered"
      : filter === "cancelled" ? o.status === "cancelled"
      : filter === "all" ? true
      : ACTIVE.includes(o.status));
  const FILTERS = [
    { id: "active", label: "En cours" },
    { id: "delivered", label: "Livrées" },
    { id: "cancelled", label: "Annulés" },
    { id: "all", label: "Toutes" },
  ];

  return (
    <div className="fade-up">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Gestion des commandes &amp; livraisons</h2>
      <p className="text-slate-400 text-sm mb-6">Commandes en cours et livrées, avec détail complet (visible par l'administrateur uniquement).</p>

      <div className="flex gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`orders-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm border transition-all ${filter === f.id ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15 hover:text-amber-300"}`}>
            {f.label} <span className="font-mono text-xs opacity-70">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 && <p className="text-slate-500 text-sm">Aucune commande dans cette catégorie.</p>}

      <div className="space-y-4" data-testid="admin-orders-list">
        {filtered.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="glass rounded-2xl p-5 border border-amber-500/10">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-amber-500/10">
              <div className="flex items-center gap-3">
                <span className="font-mono text-amber-400 text-lg font-bold">{o.order_number}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                {o.contact_requested && (
                  <span data-testid={`contact-requested-${o.order_number}`} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Recontact demandé
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("fr-FR")}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><UserIcon className="w-3 h-3" /> Client</p>
                <p className="text-slate-100 text-sm font-medium" data-testid={`order-client-name-${o.order_number}`}>{fullName(o)}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> WhatsApp</p>
                <p className="text-slate-100 text-sm font-mono" data-testid={`order-client-whatsapp-${o.order_number}`}>{o.client_whatsapp || "—"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse de livraison</p>
                <p className="text-slate-100 text-sm" data-testid={`order-address-${o.order_number}`}>{o.delivery_address}</p>
                {o.delivery_note && <p className="text-xs text-slate-400 mt-0.5 italic">Note : {o.delivery_note}</p>}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Produits</p>
              <div className="space-y-1.5" data-testid={`order-items-${o.order_number}`}>
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-[#161926] rounded-lg px-3 py-2 border border-amber-500/10">
                    <span className="text-slate-200">{it.quantity} × {it.name}</span>
                    <span className="font-mono text-slate-400">{(it.price * it.quantity).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-amber-500/10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2.5 py-1 rounded-full ${o.payment_method === "cash" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`} data-testid={`order-payment-${o.order_number}`}>
                  {o.payment_method === "cash" ? "Espèces" : "Carte bancaire"}
                </span>
                {o.delivery_fee > 0 && <span className="text-[11px] text-slate-300">dont livraison {o.delivery_fee.toFixed(2)} €</span>}
                {o.card_fee > 0 && <span className="text-[11px] text-slate-500">dont frais CB {o.card_fee.toFixed(2)} €</span>}
                {o.assigned_driver_name && <span className="text-[11px] text-slate-400">Livreur : {o.assigned_driver_name}</span>}
                {(o.status === "pending" || o.status === "assigned") && (
                  <Button data-testid={`assign-button-${o.order_number}`} onClick={() => setAssignOrder(o)}
                    size="sm" className="btn-gold rounded-lg text-xs h-8">
                    {o.status === "assigned" ? "Réattribuer" : "Attribuer un livreur"}
                  </Button>
                )}
                {o.status !== "delivered" && o.status !== "cancelled" && (
                  <Button data-testid={`cancel-button-${o.order_number}`} onClick={() => cancelOrder(o)}
                    size="sm" variant="outline" className="rounded-lg text-xs h-8 border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">
                    Annuler la commande
                  </Button>
                )}
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Total</p>
                <p className="text-xl font-bold gold-text font-mono" data-testid={`order-total-${o.order_number}`}>{o.total.toFixed(2)} €</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AssignDialog order={assignOrder} drivers={drivers} onClose={() => setAssignOrder(null)} onDone={load} />

      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent className="bg-[#10121B] border-amber-500/20 max-w-md">
          <DialogHeader><DialogTitle className="gold-text text-xl">Annuler la commande {cancelTarget?.order_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 mb-1">Choisissez un motif d'annulation. Il sera affiché au client.</p>
          <div className="space-y-2 mt-1" data-testid="cancel-reason-list">
            {CANCEL_REASONS.map((r) => (
              <button key={r} type="button" data-testid={`cancel-reason-${r}`} onClick={() => setCancelReason(r)}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${cancelReason === r ? "bg-rose-500/15 text-rose-200 border-rose-500/50" : "bg-[#161926] text-slate-300 border-amber-500/15 hover:border-rose-500/30"}`}>
                {cancelReason === r ? "✓ " : ""}{r}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="cancel-dialog-dismiss" variant="outline" onClick={() => setCancelTarget(null)} className="flex-1 rounded-xl border-amber-500/20 text-slate-300">Retour</Button>
            <Button data-testid="confirm-cancel-button" onClick={confirmCancel} className="flex-1 rounded-xl bg-rose-500/80 hover:bg-rose-500 text-white">Confirmer l'annulation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignDialog({ order, drivers, onClose, onDone }) {
  const [driverId, setDriverId] = useState("");
  useEffect(() => { setDriverId(""); }, [order]);
  const submit = async () => {
    if (!driverId) { toast.error("Choisir un livreur"); return; }
    try {
      await api.patch(`/admin/orders/${order.id}/assign`, { driver_id: driverId });
      toast.success(`${order.order_number} attribuée`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#10121B] border-amber-500/20 max-w-sm">
        <DialogHeader><DialogTitle className="gold-text text-xl">Attribuer {order?.order_number}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger data-testid="assign-driver-select" className="bg-[#161926] border-amber-500/20 text-slate-100"><SelectValue placeholder="Sélectionner un livreur" /></SelectTrigger>
            <SelectContent className="bg-[#161926] border-amber-500/20 text-slate-100">
              {drivers.map((d) => (
                <SelectItem key={d.id} value={d.id} data-testid={`driver-option-${d.id}`}>{d.name} · {d.active_orders} en cours{d.active ? "" : " · inactif"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="confirm-assign-button" onClick={submit} className="w-full btn-gold rounded-xl">Confirmer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_PRODUCT = { name: "", description: "", category: "", price: "", image_url: "", units_per_sale: 1, active: true };

function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...EMPTY_PRODUCT }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p, price: String(p.price) }); setOpen(true); };
  const del = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    await api.delete(`/admin/products/${p.id}`); toast.success("Produit supprimé"); load();
  };

  if (loading) return <Loader />;

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-3xl font-bold text-slate-100">Catalogue</h2>
        <p className="text-slate-400 text-sm">Gérez les produits, prix et photos. Le stock se gère par livreur dans l'onglet Livreurs.</p></div>
        <Button data-testid="add-product-button" onClick={openNew} className="btn-gold rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="glass rounded-2xl overflow-hidden card-hover">
            <div className="flex">
              <img src={p.image_url} alt={p.name} className="w-24 h-24 object-cover" />
              <div className="p-3 flex-1 min-w-0">
                <p className="text-[10px] font-mono uppercase text-amber-400/70">{p.category}</p>
                <h3 className="text-sm font-semibold text-slate-100 line-clamp-1">{p.name}</h3>
                <p className="text-amber-400 font-mono text-sm mt-0.5">{p.price.toFixed(2)} €</p>
                <p className="text-xs mt-0.5 text-slate-400" data-testid={`product-units-${p.id}`}>{p.units_per_sale || 1} u./vente · {p.category}</p>
              </div>
            </div>
            <div className="flex border-t border-amber-500/10">
              <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="flex-1 py-2 text-xs text-slate-300 hover:text-amber-300 flex items-center justify-center gap-1"><Pencil className="w-3 h-3" /> Modifier</button>
              <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="flex-1 py-2 text-xs text-slate-300 hover:text-rose-300 border-l border-amber-500/10 flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>
            </div>
          </div>
        ))}
      </div>
      <ProductDialog open={open} onOpenChange={setOpen} product={editing} onDone={load} />
    </div>
  );
}

function ProductDialog({ open, onOpenChange, product, onDone }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (product) setForm(product); }, [product]);
  const isEdit = product && product.id;

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/admin/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: r.data.url }));
      toast.success("Photo ajoutée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.category || form.price === "") { toast.error("Nom, catégorie et prix requis"); return; }
    setSaving(true);
    const payload = { name: form.name, description: form.description || "", category: form.category,
      price: parseFloat(form.price), image_url: form.image_url || "", units_per_sale: parseFloat(form.units_per_sale) || 1, active: true };
    try {
      if (isEdit) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Produit modifié" : "Produit ajouté");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#10121B] border-amber-500/20 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="gold-text text-xl">{isEdit ? "Modifier" : "Nouveau produit"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Nom"><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#161926] border-amber-500/20 text-slate-100" /></Field>
          <Field label="Catégorie"><Input data-testid="product-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Gourmet, Boissons…" className="bg-[#161926] border-amber-500/20 text-slate-100" /></Field>
          <Field label="Prix (€)"><Input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#161926] border-amber-500/20 text-slate-100" /></Field>
          <Field label="Unités déduites du stock (par article vendu)">
            <Input data-testid="product-units-per-sale" type="number" min="0.5" step="0.5" value={form.units_per_sale} onChange={(e) => setForm({ ...form, units_per_sale: e.target.value })} className="bg-[#161926] border-amber-500/20 text-slate-100" />
            <p className="text-[11px] text-slate-500 mt-1">Ex : un lot de 5 déduit 5 unités ; une vente au détail peut valoir 0,5. Le client ne voit jamais cette valeur.</p>
          </Field>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Photo du produit</Label>
            {form.image_url ? (
              <div className="relative rounded-xl overflow-hidden border border-amber-500/20 mb-2">
                <img src={form.image_url} alt="aperçu" className="w-full h-40 object-cover" data-testid="product-image-preview" />
              </div>
            ) : null}
            <label data-testid="product-image-upload" className={`flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed cursor-pointer text-sm transition-all ${uploading ? "opacity-60" : "hover:border-amber-500/50"} border-amber-500/30 text-amber-200 bg-[#161926]`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {uploading ? "Téléversement..." : (form.image_url ? "Changer la photo" : "Choisir une photo depuis la galerie")}
              <input type="file" accept="image/*" className="hidden" onChange={onPickImage} disabled={uploading} />
            </label>
          </div>
          <Field label="Description"><Textarea data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#161926] border-amber-500/20 text-slate-100" /></Field>
          <Button data-testid="save-product-button" onClick={save} disabled={saving || uploading} className="w-full btn-gold rounded-xl">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [stripeForm, setStripeForm] = useState({ stripe_mode: "test", stripe_publishable_key: "", stripe_secret_key: "" });
  const [savingStripe, setSavingStripe] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setStripeForm({ stripe_mode: r.data.stripe_mode || "test", stripe_publishable_key: r.data.stripe_publishable_key || "", stripe_secret_key: "" });
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveStripe = async () => {
    setSavingStripe(true);
    try {
      await api.put("/admin/settings", { use_manual: true, ...stripeForm });
      toast.success("Configuration Stripe enregistrée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStripe(false); }
  };
  const useManaged = async () => {
    if (!window.confirm("Revenir au Stripe géré automatiquement ?")) return;
    try { await api.put("/admin/settings", { use_manual: false }); toast.success("Stripe géré réactivé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <Loader />;

  const isLive = stripeForm.stripe_mode === "live";

  return (
    <div className="fade-up max-w-2xl">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Paramètres</h2>
      <p className="text-slate-400 text-sm mb-6">Configuration des intégrations et de l'équipe.</p>

      <CardFeeSetting />

      <div className="glass rounded-2xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-200 flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-400" /> Stripe</h3>
          <span data-testid="stripe-mode-badge" className={`text-xs px-2.5 py-1 rounded-full font-mono ${settings.stripe_mode === "live" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
            {settings.stripe_mode === "live" ? "LIVE" : "TEST"}{settings.stripe_manual ? " · manuel" : " · géré"}
          </span>
        </div>
        <p className="text-[12px] text-slate-400 mb-4 leading-relaxed">
          Configurez Stripe manuellement en saisissant vos propres clés API. Sélectionnez le mode <span className="text-amber-300">Test</span> (clés <span className="font-mono">sk_test / pk_test</span>) ou <span className="text-emerald-300">Live</span> (clés <span className="font-mono">sk_live / pk_live</span>) pour encaisser de vrais paiements.
        </p>

        <Label className="text-slate-300 text-xs mb-2 block">Mode</Label>
        <div className="flex gap-2 mb-4">
          <button type="button" data-testid="stripe-mode-test" onClick={() => setStripeForm({ ...stripeForm, stripe_mode: "test" })}
            className={`flex-1 py-2 rounded-xl text-sm border transition-all ${!isLive ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15"}`}>Test</button>
          <button type="button" data-testid="stripe-mode-live" onClick={() => setStripeForm({ ...stripeForm, stripe_mode: "live" })}
            className={`flex-1 py-2 rounded-xl text-sm border transition-all ${isLive ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15"}`}>Live (production)</button>
        </div>

        <div className="space-y-3">
          <Field label="Clé publique (Publishable key)">
            <Input data-testid="stripe-publishable-input" value={stripeForm.stripe_publishable_key}
              onChange={(e) => setStripeForm({ ...stripeForm, stripe_publishable_key: e.target.value })}
              placeholder={isLive ? "pk_live_..." : "pk_test_..."} className="bg-[#161926] border-amber-500/20 text-slate-100 font-mono text-xs" />
          </Field>
          <Field label="Clé secrète (Secret key)">
            <Input data-testid="stripe-secret-input" type="password" value={stripeForm.stripe_secret_key}
              onChange={(e) => setStripeForm({ ...stripeForm, stripe_secret_key: e.target.value })}
              placeholder={settings.stripe_secret_key_set ? `•••••••• (enregistrée ...${settings.stripe_secret_key_last4})` : (isLive ? "sk_live_..." : "sk_test_...")}
              className="bg-[#161926] border-amber-500/20 text-slate-100 font-mono text-xs" />
            <p className="text-[11px] text-slate-500 mt-1">Laissez vide pour conserver la clé secrète déjà enregistrée.</p>
          </Field>
          <div className="flex items-center gap-2">
            <Button data-testid="save-stripe-button" onClick={saveStripe} disabled={savingStripe} className="btn-gold rounded-xl">
              {savingStripe ? "Enregistrement..." : "Enregistrer les clés Stripe"}
            </Button>
            {settings.stripe_manual && (
              <button data-testid="use-managed-stripe" onClick={useManaged} className="text-xs text-slate-400 hover:text-amber-300 underline">
                Revenir au Stripe géré
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryPicker({ selected, onToggle, categories }) {
  if (categories.length === 0)
    return <p className="text-xs text-slate-500">Aucune catégorie dans le catalogue. Ajoutez des produits d'abord.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((c) => {
        const on = (selected || []).includes(c);
        return (
          <button key={c} type="button" data-testid={`category-toggle-${c}`} onClick={() => onToggle(c)}
            className={`px-3 py-1.5 rounded-full text-xs transition-all border ${on ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-[#161926] text-slate-400 border-amber-500/15 hover:text-amber-300"}`}>
            {on ? "✓ " : ""}{c}
          </button>
        );
      })}
    </div>
  );
}

function Clients() {
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState([]);
  const EMPTY = { whatsapp: "", first_name: "", last_name: "", email: "", address: "", note: "", allowed_categories: [] };
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const shown = filterClients(clients, q, status);
  const nActive = clients.filter((c) => c.activated).length;
  const load = useCallback(() => {
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/categories").then((r) => setCats(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggleCat = (list, c) => (list || []).includes(c) ? list.filter((x) => x !== c) : [...(list || []), c];
  const add = async () => {
    if (!form.whatsapp.trim()) { toast.error("Numéro WhatsApp requis"); return; }
    try { await api.post("/admin/clients", form); toast.success("Fiche client créée"); setForm(EMPTY); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/clients/${editing.id}`, {
        first_name: editing.first_name || "", last_name: editing.last_name || "",
        email: editing.email || "", address: editing.address || "", note: editing.note || "",
        allowed_categories: editing.allowed_categories || [] });
      toast.success("Fiche mise à jour"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce client ?")) return; await api.delete(`/admin/clients/${id}`); toast.success("Client supprimé"); load(); };
  return (
    <div className="fade-up max-w-2xl">
      <h2 className="text-3xl font-bold text-slate-100 mb-1">Comptes Clients</h2>
      <p className="text-slate-400 text-sm mb-6">Créez les accès clients par numéro WhatsApp. Le client choisit son mot de passe lors de sa première connexion. Personne ne peut s'inscrire seul.</p>
      <div className="glass rounded-2xl p-5 mb-5">
        <h3 className="text-lg font-semibold text-slate-200 mb-3">Nouvelle fiche client</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input data-testid="new-client-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Numéro WhatsApp *" className="bg-[#161926] border-amber-500/20 text-slate-100" />
          <Input data-testid="new-client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#161926] border-amber-500/20 text-slate-100" />
          <Input data-testid="new-client-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="bg-[#161926] border-amber-500/20 text-slate-100" />
          <Input data-testid="new-client-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="bg-[#161926] border-amber-500/20 text-slate-100" />
          <Input data-testid="new-client-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse" className="bg-[#161926] border-amber-500/20 text-slate-100 sm:col-span-2" />
          <Input data-testid="new-client-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note interne (visible admin seulement)" className="bg-[#161926] border-amber-500/20 text-slate-100 sm:col-span-2" />
        </div>
        <div className="mt-4">
          <Label className="text-slate-300 text-xs mb-2 block">Catégories visibles par ce client</Label>
          <CategoryPicker categories={cats} selected={form.allowed_categories} onToggle={(c) => setForm({ ...form, allowed_categories: toggleCat(form.allowed_categories, c) })} />
          <p className="text-[11px] text-slate-500 mt-2">Sans catégorie sélectionnée, le client ne verra aucun produit.</p>
        </div>
        <Button data-testid="add-client-button" onClick={add} className="btn-gold rounded-xl mt-3"><Plus className="w-4 h-4 mr-1" /> Créer la fiche</Button>
      </div>
      <div className="glass rounded-2xl p-5">
        <h3 className="text-lg font-semibold text-slate-200 mb-3">Clients ({shown.length}{shown.length !== clients.length ? ` / ${clients.length}` : ""})</h3>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-amber-400/70 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, prénom ou WhatsApp" className="pl-9 bg-[#161926] border-amber-500/20 text-slate-100" />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {[["all", `Tous (${clients.length})`], ["active", `Activé (${nActive})`], ["pending", `En attente 1ʳᵉ connexion (${clients.length - nActive})`]].map(([k, label]) => (
            <button key={k} data-testid={`client-filter-${k}`} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${status === k ? "btn-gold" : "bg-[#161926] border border-amber-500/20 text-slate-300 hover:text-amber-300"}`}>{label}</button>
          ))}
        </div>
        <div className="space-y-2">
          {shown.map((c) => (
            <div key={c.id} data-testid={`admin-client-${c.id}`} className="flex items-center justify-between bg-[#161926] rounded-lg p-3 border border-amber-500/10">
              <div className="min-w-0">
                <p className="text-sm text-slate-100 font-semibold truncate">{c.name || "Sans nom"} <span className="font-mono text-amber-300 font-normal">· {c.whatsapp}</span></p>
                <p className="text-xs text-slate-500 truncate">{c.email || "—"} · <span className={c.activated ? "text-emerald-300" : "text-amber-300"}>{c.activated ? "Activé" : "En attente 1ʳᵉ connexion"}</span></p>
                <p className="text-xs mt-0.5"><span className="text-slate-400">{c.orders} commande(s)</span> · <span className="text-emerald-300 font-mono">{Number(c.total_collected || 0).toFixed(2)} € encaissés</span></p>
                <p className="text-[11px] mt-0.5 text-slate-500 truncate">Catégories : {(c.allowed_categories && c.allowed_categories.length) ? <span className="text-amber-300">{c.allowed_categories.join(", ")}</span> : <span className="text-rose-400">aucune (catalogue masqué)</span>}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`edit-client-${c.id}`} onClick={() => setEditing(c)} className="text-amber-300 hover:text-amber-200 p-1"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-client-${c.id}`} onClick={() => del(c.id)} className="text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {clients.length === 0 && <p className="text-sm text-slate-600 text-center py-4">Aucun client pour le moment.</p>}
          {clients.length > 0 && shown.length === 0 && <p data-testid="client-no-results" className="text-sm text-slate-600 text-center py-4">Aucun client ne correspond.</p>}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#10121B] border-amber-500/20 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="gold-text text-xl">Fiche client</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">Informations confidentielles, visibles par l'administrateur uniquement.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <p className="text-sm font-mono text-amber-300">{editing.whatsapp}</p>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="edit-client-firstname" value={editing.first_name || ""} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} placeholder="Prénom" className="bg-[#161926] border-amber-500/20 text-slate-100" />
                <Input data-testid="edit-client-lastname" value={editing.last_name || ""} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} placeholder="Nom" className="bg-[#161926] border-amber-500/20 text-slate-100" />
              </div>
              <Input data-testid="edit-client-email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email" className="bg-[#161926] border-amber-500/20 text-slate-100" />
              <Input data-testid="edit-client-address" value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="Adresse" className="bg-[#161926] border-amber-500/20 text-slate-100" />
              <Input data-testid="edit-client-note" value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="Note interne" className="bg-[#161926] border-amber-500/20 text-slate-100" />
              <div>
                <Label className="text-slate-300 text-xs mb-2 block">Catégories visibles par ce client</Label>
                <CategoryPicker categories={cats} selected={editing.allowed_categories} onToggle={(c) => setEditing({ ...editing, allowed_categories: toggleCat(editing.allowed_categories, c) })} />
                <p className="text-[11px] text-slate-500 mt-2">Sans catégorie sélectionnée, le client ne verra aucun produit.</p>
              </div>
              <div className="flex gap-4 text-sm bg-[#0F1320] rounded-lg p-3 border border-amber-500/10">
                <span className="text-slate-400">Commandes : <span className="text-slate-100 font-mono">{editing.orders}</span></span>
                <span className="text-slate-400">Encaissé : <span className="text-emerald-300 font-mono">{Number(editing.total_collected || 0).toFixed(2)} €</span></span>
              </div>
              <Button data-testid="save-client-button" onClick={saveEdit} className="w-full btn-gold rounded-xl">Enregistrer la fiche</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-slate-300 text-xs mb-1 block">{label}</Label>{children}</div>;
}
function Loader() { return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>; }
